import type { ViewStateContract } from "@wags/wags-api-client/session-state.contract.ts";

export type ViewState = ViewStateContract;

const QUERY_KEY_ALLOWLIST = new Set([
    "q",
    "breedSlugs",
    "size",
    "sex",
    "priceMin",
    "priceMax",
    "maxDistanceMiles",
    "lat",
    "lng",
    "sortBy",
]);

const MAX_QUERY_VALUE_CHARS = 120;

function sanitizeQueryValue(raw: string): string {
    return raw.replace(/[\r\n]+/g, " ").slice(0, MAX_QUERY_VALUE_CHARS);
}

export function decodeQuery(
    search: Record<string, ViewState["slices"][string] | undefined>,
): Record<string, string> {
    const out: Record<string, string> = {};
    for (const [key, value] of Object.entries(search)) {
        if (!QUERY_KEY_ALLOWLIST.has(key)) continue;
        if (value === undefined || value === null || value === "") continue;
        const raw =
            typeof value === "object" ? JSON.stringify(value) : String(value);
        out[key] = sanitizeQueryValue(raw);
    }
    return out;
}

export function mergeViewState(
    base: { page: string; query: Record<string, string> },
    slices: ViewState["slices"],
    updatedAt: number,
): ViewState {
    return { page: base.page, query: base.query, slices, updatedAt };
}

export function stableStringify(value: ViewState["slices"][string]): string {
    return JSON.stringify(sortJson(value));
}

function sortJson(
    value: ViewState["slices"][string],
): ViewState["slices"][string] {
    if (Array.isArray(value)) return value.map(sortJson);
    if (value && typeof value === "object") {
        return Object.fromEntries(
            Object.entries(value)
                .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
                .map(([key, val]) => [key, sortJson(val)]),
        );
    }
    return value;
}

export function serializeForChangeDetection(state: ViewState): string {
    return stableStringify({
        page: state.page,
        query: state.query,
        slices: state.slices,
    });
}

export function shouldPublish(prev: string | null, next: string): boolean {
    return prev !== next;
}
